import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useTable} from 'react-table';
import * as actions from '../../_redux/invoicesActions';
import * as columnFormatters from './column-formatters';
import * as commonColumnFormatters from '../../../_common/column-formatters';
import {useInvoicesUIContext} from '../InvoicesUIContext';
import useInfiniteScrollControl from '../../../_common/components/_custom-hooks/useInfiniteScrollControl';
import CommonGrid from '../../../_common/components/CommonGrid/CommonGrid';
import SortingColumnHeader from './SortingColumnHeader';

export function InvoicesTable() {
  const intl = useIntl();
  const [tableRows, setTableRows] = useState([]);
  const columns = useMemo(() => [
    {
      accessor: 'invoiceId',
      Header: intl.formatMessage({
        id: 'invoices-column-invoiceID', defaultMessage: 'Invoice ID'
      })
    },
    {
      accessor: 'agent',
      Header: intl.formatMessage({
        id: 'invoices-column-agent', defaultMessage: 'Agent'
      })
    },
    {
      accessor: 'customer',
      Header: intl.formatMessage({
        id: 'invoices-column-customer', defaultMessage: 'Customer'
      })
    },
    {
      accessor: 'date',
      Header:
        <SortingColumnHeader
          header={'invoices-column-date'}
          defaultMessage={'Date'}
          fieldName={'datetime'}
        />
    },
    {
      accessor: 'amount',
      Header:
        <SortingColumnHeader
          header={'invoices-column-amount'}
          defaultMessage={'Amount'}
          fieldName={'total.withVat'}
        />
    },
    {
      accessor: 'status',
      Header: intl.formatMessage({
        id: 'invoices-column-status', defaultMessage: 'Status'
      })
    },
    {
      accessor: 'reminder',
      Header: intl.formatMessage({
        id: 'invoices-column-reminder', defaultMessage: 'Reminder'
      })
    },
    {
      accessor: 'receiptsList',
      Header: intl.formatMessage({
        id: 'invoices-column-receiptsList', defaultMessage: 'Receipts list'
      })
    },
    {
      accessor: 'actions',
      Header: intl.formatMessage({
        id: 'invoices-column-actions', defaultMessage: 'Actions'
      })
    },
  ], [intl]);
  const data = useMemo(() => [
    ...tableRows.map((item) => {
      return {
        invoiceId: commonColumnFormatters.BoldColumnFormatter(item._id),
        agent: [
          commonColumnFormatters.BoldColumnFormatter(item.agentId),
          commonColumnFormatters.NormalColumnFormatter(item.agentName),
        ],
        customer: [
          commonColumnFormatters.BoldColumnFormatter(item.customerID),
          commonColumnFormatters.NormalColumnFormatter(item.customerName),
        ],
        date: commonColumnFormatters.DateColumnFormatter(item.datetime),
        amount: columnFormatters.AmountWithVATColumnFormatter(item),
        status: commonColumnFormatters.BoldColumnFormatter(item.statusName),
        reminder: [
          commonColumnFormatters.BoldColumnFormatter(item.reminderCount),
          commonColumnFormatters.DateColumnFormatter(item.lastReminderDate)
        ],
        receiptsList: commonColumnFormatters.SeveralItemsColumnFormatter(item.receipts),
        actions: columnFormatters.InvoiceActionsColumnFormatter(item),
      };
    })
  ], [tableRows]);

  const infiniteScrollProps = useInfiniteScrollControl({
    currentStateStoreId: 'invoices',
    UIContext: useInvoicesUIContext(),
    getNewEntities: actions.getInvoices,
    getNextEntities: actions.getNextInvoices,
    onEntitiesChange: entities => {
      setTableRows(entities);
    },
    dataLength: tableRows.length,
    scrollableTarget: 'scrollContainer',
    scrollThreshold: 0.9,
    loader: (
      <div className={'spinner-container'}>
        <span className={'spinner spinner-md spinner-primary'}/>
      </div>
    ),
    style: {
      overflow: 'inherit'
    },
  });

  return (
    <CommonGrid
      infiniteScrollProps={infiniteScrollProps}
      tableProps={useTable({columns, data})}
      customTableStyles={'invoices-table'}
    />
  );
}
